<template>
  <div class="ProjectCard" @click="redirectToProjectDetails()">
    <div class="ProjectCard-card">
      <div :class="`ProjectCard-header ${projectNature}`">
        <span class="ProjectCard-type">
          <span
            class="BookmarkCard-tag"
            :style="{
              'padding-bottom': project.statut == 'ACCEPTE_SOUS_RESERVE_REFERENT' ? '60px' : '',
            }"
          >
            <span v-html="projectService.getProjectStatusLabel(project, loggedUser)"></span>
          </span>
        </span>
        <span
          v-if="projectState"
          :title="$t('myProjects.filter-labels.etats') + ' : ' + $t(project.etat)"
        >
          <span v-if="projectState === 'active'" :class="`ProjectCard-state ${projectState}`">
            <b-icon icon="play-circle" size="is-medium" type="is-success" />
            {{ $t("myProjects.active") }}
          </span>
          <span v-else-if="projectState === 'paused'" :class="`ProjectCard-state ${projectState}`">
            <b-icon icon="pause-circle" size="is-medium" type="is-warning" />
            {{ $t("myProjects.paused") }}
          </span>
          <span v-else-if="projectState === 'stopped'" :class="`ProjectCard-state ${projectState}`">
            <b-icon icon="stop-circle" size="is-medium" type="is-danger" />
            {{ $t("myProjects.stopped") }}
          </span>
        </span>
      </div>
      <div class="ProjectCard-content">
        <div class="ProjectCard-buttons">
          <b-button
            v-if="project.typeDossier == 'DOSSIER_INSCRIPTION' && project.etat != 'ARCHIVE'"
            class="ProjectCard-delete-button"
            type="is-danger"
            icon-left="trash"
            :title="$t('ProjectCard.delete')"
            rounded
            @click="deleteProject"
          />

          <b-button class="ProjectCard-consult-button" type="is-primary" rounded>
            {{ projectService.getProjectLightConsultActionLabel(project, loggedUser) }}
          </b-button>
        </div>
        <div v-if="project.etablissements.length > 0" class="ProjectCard-text">
          {{ project.etablissements[0].nom }}
        </div>
        <div class="ProjectCard-text">{{ project.natureAireEducative }}</div>
        <div class="ProjectCard-text name">{{ project.nom }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelLightDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import RouterUtils from "@/router/RouterUtils"
import { BButton, BIcon } from "buefy"

const PROJECT_STATE_STRINGS = {
  EN_COURS_DE_CREATION: "en cours de création",
  ACTIF: "active",
  EN_PAUSE: "paused",
  ARCHIVE: "stopped",
}

const PROJECT_NATURE_STRINGS = {
  ATE: "ate",
  AME: "ame",
  UNDEFINED: "undefined",
}

export default defineComponent({
  name: "ProjectCard",
  components: {
    BIcon,
    BButton,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelLightDTO>,
      required: true,
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
  },

  emits: ["delete-project"],
  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      projectService: ProjectService.INSTANCE,
      projectState: "",
      projectNature: "",
    }
  },
  created(): void {
    if (this.project && this.project.etat) {
      this.projectState = PROJECT_STATE_STRINGS[this.project.etat]
      this.projectNature = PROJECT_NATURE_STRINGS[this.project.natureAireEducative] || "undefined"
    }
  },
  methods: {
    deleteProject(e: Event): void {
      e.stopPropagation()
      this.infoReportingService.dialogWithCancel(
        this.$t("ProjectCard.archive-popup.confirmation").toString(),
        this.$t("ProjectCard.archive-popup.are-you-sure").toString(),
        this.$t("ProjectCard.archive-popup.confirm").toString(),
        () => {
          this.projectService.deleteProject(this.project.projetId).then(() => {
            this.$emit("delete-project", this.project)
          })
        },
        () => {
          //do nothing
        },
        "is-danger",
      )
    },

    async redirectToProjectDetails(): Promise<void> {
      try {
        await this.projectService.checkRightsAccessToProject(this.project.projetId)
        this.$router.push(
          RouterUtils.getRouteForProjectDetails(this.project.projetId, this.project.id),
        )
      } catch (e: unknown) {
        this.infoReportingService.error(this.$t("projet.not-allowed").toString(), e as Error)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

$card-height: 400px;
$card-width: 300px;
$content-height: 200px;
$card-header-height: $card-height - $content-height;
$margin-top: 2rem;

.ProjectCard {
  cursor: pointer;
  position: relative;
  height: $card-height;
  margin-top: $margin-top;
}

.ProjectCard-card {
  width: $card-width;
  height: $card-height;
  border-radius: 10px;
  border: 1px solid $bookmarkcard-border-color;
}

.ProjectCard-header {
  height: $card-header-height;
  border-top: 1px solid $bookmarkcard-border-color;
  display: flex;
  border-radius: 10px 10px 0 0;
  background-color: $placeholder-default-color;

  &.ate {
    background: $ofb-vert-3;
    background-image: url("/img/scarabee.svg");
    background-repeat: no-repeat;
    background-position: center;
  }

  &.ame {
    background: $ofb-bleu-2;
    background-image: url("/img/poisson.svg");
    background-repeat: no-repeat;
    background-position: center;
  }

  &.undefined {
    background: $yellow-v2;
    background-image: url("/img/scarabee.svg"), url("/img/poisson.svg");
    background-repeat: no-repeat, no-repeat;
    background-position: left, right;
  }
}

.ProjectCard-state {
  display: flex;
  align-items: center;
  background-color: white;
  height: 2rem;
  border-radius: 10% 0 0 10%;
  margin: 0.5rem 0 0.5rem 0.5rem;
  padding-right: 1rem;
  position: absolute;
  right: 0;

  &.active {
    color: $success;
  }

  &.paused {
    color: $warning;
  }

  &.stopped {
    color: $danger;
  }
}

.ProjectCard-content {
  height: $content-height;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 10px 10px;
  position: relative;
}

.ProjectCard-text {
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  &.name {
    font-weight: 700;
    margin-top: 20px;
  }
}

.ProjectCard-type {
  .BookmarkCard-tag {
    background-color: $ofb-bleu-1;
    width: auto;
    box-shadow: 0 5px 3px 0 rgba(50, 50, 50, 0.4);
    text-transform: initial;
    font-style: italic;
    padding: 0 0.5em 0.25em 0.5rem;
    margin-left: 12px;
  }
}

.ProjectCard-buttons {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: absolute;
  transform: translateY(-50%);
  top: 0;
}

.ProjectCard-consult-button.button.is-primary {
  margin-right: 0.75rem;
  border: solid 3px white;
}

.ProjectCard-delete-button.button.is-danger {
  position: absolute;
  left: 0.75rem;
  border: solid 3px white;
}
</style>
